// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// GAMI sweep special-test panel controller: runtime, persistence, and rendering
// for the "Special tests" GAMI sweep workflow on the Explorer chart.
//
// - gamiAircraftProfile / specialTestDetectionWindows / current*EngineDescriptors:
//   profile + engine-scope helpers for the selected flight's GAMI sweep.
// - load/save/delete GAMI special-test results against the persistence API,
//   plus the saved-result selection + "unsaved changes" bookkeeping.
// - sweep-window marking, clock<->elapsed parsing, and series selection driven
//   by the Explorer scrubber.
// - gami*Html builders + renderGamiSpecialTestsPanel: the panel markup.
//
// State that lives HERE (moved out of app.js because nothing outside the GAMI
// panel mutates it): savedGamiSpecialTestResults, gamiSpecialTestSaveState,
// lastSavedGamiAnalysisKey, selectedSavedGamiSpecialTestResultId,
// selectedGamiSeriesSweepIndex. Shared app state (flightData, selectedFlight,
// gamiSpecialTestsState, the flight-report metadata caches, ...) is injected via
// getter/accessor dependencies so it stays in sync with the rest of app.js.

import {
  analyzeMarkedGamiSweep,
  gamiEgtKeys,
  gamiFuelFlowKey,
  gamiSweepEngineIdentity,
  gamiSweepPeakDisplayRows,
  gamiSweepWithTitAtPeaks,
} from "../analysis/gami-sweep.js";
import { savedGamiSeries } from "./gami-saved-series.js";
import { engineLabel } from "./aircraft-profile-model.js";
import { databaseFlightSpecialTestEndpoint } from "../core/config.js";
import {
  engineById,
  engineDescriptorsForData,
  projectFlightDataForEngine,
} from "../core/engines.js";
import { formatElapsedTime } from "../core/format.js";
import { extentFromValues } from "../core/math.js";
import { reportFileName } from "../reporting/pdf-helpers.js";
import { cylinderKeys } from "../core/schema.js";
import { escapeHtml } from "../core/html.js";
import { leanFindStripHtml } from "./lean-find-strip.js";
import { formatPointTime } from "../core/format-point-time.js";

export function createGamiSpecialTestsController({
  aircraftProfileForEntry,
  appReadOnlyMode,
  availableSeriesKeys,
  hideExplorerGamiCursorTime,
  readOnlyActionMessage,
  renderAiSummaryPanel,
  renderAircraftFlightsPanel,
  resetGeneratedReport,
  revokeCachedFlightReport,
  scheduleChartDraw,
  scheduleDrawAll,
  selectReportPanel,
  selectedDatabaseFlightId,
  syncReadOnlyUi,
  syncSeriesControls,
  trendCacheKey,
  flightReportMetadataKey,
  getFlightData,
  getSelectedFlight,
  getFlightManifest,
  getAircraftProfiles,
  getCurrentParserMetadata,
  getLastRequestedFlightId,
  getTrendCache,
  getGamiSpecialTestsState,
  getActiveSeries,
  getFlightReportMetadataChecked,
  getFlightReportMetadataPending,
  getFlightReportStoredMetadata,
  getExplorerPilotObservationSelectionActive = () => false,
  chartGestureHintBaseText = () => "Drag to zoom • Right-drag to pan",
  onOpenGamiPanel = () => {},
}) {
  // Stable shared collections (const in app.js, never reassigned) — capture the
  // live reference once; mutations propagate because it's the same object.
  const gamiSpecialTestsState = getGamiSpecialTestsState();
  const activeSeries = getActiveSeries();
  const flightReportMetadataChecked = getFlightReportMetadataChecked();
  const flightReportMetadataPending = getFlightReportMetadataPending();
  const flightReportStoredMetadata = getFlightReportStoredMetadata();

  // GAMI-only mutable state (moved out of app.js).
  let savedGamiSpecialTestResults = [];
  let gamiSpecialTestSaveState = { status: "idle", message: "" };
  let lastSavedGamiAnalysisKey = "";
  let selectedSavedGamiSpecialTestResultId = "";
  let selectedGamiSeriesSweepIndex = -1;

  // Saved sweeps per flight, so re-selecting a flight does not refetch them
  // on every load. Safe to reuse for the whole session: special_test_results
  // only change through the save/delete mutations below (which write through),
  // re-analysis does not touch them, and flight ids are stable (a re-ingest
  // mints new ids). Empty results are cached too — a flight with no saved
  // sweeps would otherwise 404 on every select. Bounded LRU against leaks.
  const SAVED_GAMI_RESULTS_CACHE_MAX = 24;
  const savedGamiSpecialTestResultsCache = new Map();

  function rememberSavedGamiSpecialTests(flightId, results) {
    if (!flightId) return;
    savedGamiSpecialTestResultsCache.delete(flightId);
    savedGamiSpecialTestResultsCache.set(flightId, results);
    if (savedGamiSpecialTestResultsCache.size > SAVED_GAMI_RESULTS_CACHE_MAX) {
      savedGamiSpecialTestResultsCache.delete(savedGamiSpecialTestResultsCache.keys().next().value);
    }
  }

  function applySavedGamiSpecialTestResults(results) {
    savedGamiSpecialTestResults = results;
    if (!results.length) return;
    gamiSpecialTestSaveState = {
      status: "saved",
      message: `${results.length} saved sweep${results.length === 1 ? "" : "s"}`,
    };
  }

  function getSavedGamiSpecialTestResults() {
    return savedGamiSpecialTestResults;
  }
  function getSelectedSavedGamiSpecialTestResultId() {
    return selectedSavedGamiSpecialTestResultId;
  }

function gamiAircraftProfile() {
  return aircraftProfileForEntry(getSelectedFlight())
    ?? (getSelectedFlight()?.aircraft ? getAircraftProfiles()?.[getSelectedFlight().aircraft] ?? null : null);
}

function gamiAircraftProfileInfo(profile = gamiAircraftProfile()) {
  const fuelSystem = String(profile?.engine?.fuelSystem ?? "").trim();
  return {
    fuelSystem,
    fuelInjected: /inject/i.test(fuelSystem),
    carbureted: /carb/i.test(fuelSystem),
    turbocharged: profile?.turbo?.isTurbo === true,
    maxTitF: Number.isFinite(Number(profile?.turbo?.maxTitF)) ? Number(profile.turbo.maxTitF) : null,
  };
}

function gamiLoggedTitAvailable(data = getFlightData()) {
  return ["tit", "tit1", "tit2"].some((key) => data.some((point) => Number.isFinite(point?.[key])));
}

function specialTestDetectionWindows() {
  const window = gamiSpecialTestsState.window;
  if (!window) return [];
  const engine = selectedGamiEngine();
  const enginePrefix = engine && currentGamiEngineDescriptors().length > 1
    ? `${String(engine.label ?? "Engine").replace(/\s+Engine$/i, "")} `
    : "";
  return [{
    key: "gami",
    sourceKey: "gami",
    label: `${enginePrefix}GAMI sweep`,
    evidenceLabel: `${enginePrefix}GAMI sweep`,
    startTime: window.startTime,
    endTime: window.endTime,
  }];
}

function resetGamiSpecialTestSelectionState() {
  gamiSpecialTestsState.window = null;
  gamiSpecialTestsState.selecting = false;
  gamiSpecialTestsState.engineId = "";
}

function clearGamiSpecialTestPersistenceState() {
  savedGamiSpecialTestResults = [];
  gamiSpecialTestSaveState = { status: "idle", message: "" };
  lastSavedGamiAnalysisKey = "";
  selectedSavedGamiSpecialTestResultId = "";
}

function gamiSavedAtLabel(result) {
  const value = result?.updated_at ?? result?.created_at;
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function gamiSavedResultWindow(result = {}) {
  const source = result ?? {};
  const resultJson = source.result_json ?? source.result ?? {};
  const startTime = Number(source.start_time ?? resultJson.startTime);
  const endTime = Number(source.end_time ?? resultJson.endTime);
  return Number.isFinite(startTime) && Number.isFinite(endTime) && endTime > startTime
    ? { startTime, endTime }
    : null;
}

function currentGamiEngineDescriptors() {
  return engineDescriptorsForData(getFlightData(), gamiAircraftProfile(), {
    parserId: getCurrentParserMetadata()?.parserId ?? getSelectedFlight()?.parser,
    parserMetadata: getCurrentParserMetadata(),
  });
}

function ensureSelectedGamiEngineId(descriptors = currentGamiEngineDescriptors()) {
  if ((descriptors?.length ?? 0) <= 1) {
    gamiSpecialTestsState.engineId = descriptors?.[0]?.id ?? "engine1";
    return gamiSpecialTestsState.engineId;
  }
  const selected = engineById(descriptors, gamiSpecialTestsState.engineId);
  if (selected) return selected.id;
  gamiSpecialTestsState.engineId = descriptors[0]?.id ?? "";
  return gamiSpecialTestsState.engineId;
}

function selectedGamiEngine() {
  const descriptors = currentGamiEngineDescriptors();
  const engineId = ensureSelectedGamiEngineId(descriptors);
  return engineById(descriptors, engineId) ?? descriptors[0] ?? null;
}

function gamiEngineOptionLabel(engine) {
  return String(engine?.label ?? engine?.shortLabel ?? "Engine").replace(/\s+Engine$/i, "");
}

function gamiResultEngineIdentity(result) {
  return gamiSweepEngineIdentity(result, currentGamiEngineDescriptors());
}

function normalizeSavedGamiSpecialTestResults(results = []) {
  const descriptors = currentGamiEngineDescriptors();
  const engineOrder = new Map((descriptors ?? []).map((engine, index) => [engine.id, index]));
  return (Array.isArray(results) ? results : [])
    .filter((result) => result && (result.test_kind === "gami-sweep" || result.testKind === "gami-sweep"))
    .slice()
    .sort((a, b) => {
      const aIdentity = gamiSweepEngineIdentity(a, descriptors);
      const bIdentity = gamiSweepEngineIdentity(b, descriptors);
      const aOrder = engineOrder.has(aIdentity.engineId) ? engineOrder.get(aIdentity.engineId) : Number.MAX_SAFE_INTEGER;
      const bOrder = engineOrder.has(bIdentity.engineId) ? engineOrder.get(bIdentity.engineId) : Number.MAX_SAFE_INTEGER;
      if (aOrder !== bOrder) return aOrder - bOrder;
      const aWindow = gamiSavedResultWindow(a);
      const bWindow = gamiSavedResultWindow(b);
      if (Number.isFinite(aWindow?.startTime) && Number.isFinite(bWindow?.startTime) && aWindow.startTime !== bWindow.startTime) {
        return aWindow.startTime - bWindow.startTime;
      }
      return String(a.created_at ?? a.updated_at ?? "").localeCompare(String(b.created_at ?? b.updated_at ?? ""));
    });
}

function gamiAnalysisKey(analysis = currentGamiSweepAnalysis()) {
  if (!analysis?.window) return "";
  const rounded = (value) => Math.round(Number(value) * 10) / 10;
  const questions = analysis.questions ?? {};
  const engineId = analysis.engine?.id ?? analysis.result?.engineId ?? gamiSpecialTestsState.engineId ?? "engine1";
  return [
    engineId,
    rounded(analysis.window.startTime),
    rounded(analysis.window.endTime),
    questions.intentionalSweep === true ? 1 : 0,
    questions.startedRich === true ? 1 : 0,
    questions.completedToPeaks === true ? 1 : 0,
    questions.repeatedSweeps === true ? 1 : 0,
    questions.titManaged === true ? 1 : 0,
  ].join("|");
}

function gamiResultForSingleSave(result = {}) {
  const { seriesSweeps, ...singleResult } = result ?? {};
  return singleResult;
}

function gamiAnalysisSaveResults(analysis = currentGamiSweepAnalysis()) {
  if (!analysis?.result) return [];
  const seriesSweeps = Array.isArray(analysis.result.seriesSweeps) ? analysis.result.seriesSweeps : [];
  const saveSeries = analysis.questions?.repeatedSweeps === true && seriesSweeps.length > 1;
  const results = saveSeries ? seriesSweeps : [analysis.result];
  return results
    .map((result) => {
      const startTime = Number(result?.startTime);
      const endTime = Number(result?.endTime);
      if (!Number.isFinite(startTime) || !Number.isFinite(endTime) || endTime <= startTime) return null;
      return {
        window: { startTime, endTime },
        result: {
          ...gamiResultForSingleSave(result),
          ...(saveSeries ? { savedSeriesWindow: analysis.searchWindow ?? analysis.window } : {}),
        },
      };
    })
    .filter(Boolean);
}

function gamiSavedResultKey(result) {
  const window = gamiSavedResultWindow(result);
  if (!window) return "";
  const rounded = (value) => Math.round(Number(value) * 10) / 10;
  const pilot = result?.pilot_context_json ?? result?.pilotContext ?? {};
  const identity = gamiResultEngineIdentity(result);
  return [
    identity.engineId,
    rounded(window.startTime),
    rounded(window.endTime),
    pilot.intentionalSweep === true ? 1 : 0,
    pilot.startedRich === true ? 1 : 0,
    pilot.completedToPeaks === true ? 1 : 0,
    pilot.repeatedSweeps === true ? 1 : 0,
    pilot.titManaged === true ? 1 : 0,
  ].join("|");
}

function gamiCurrentAnalysisAlreadySaved(analysis = currentGamiSweepAnalysis()) {
  const saveResults = gamiAnalysisSaveResults(analysis);
  if (!saveResults.length) return false;
  return saveResults.every(({ window, result }) => {
    const key = gamiAnalysisKey({ ...analysis, window, result });
    return Boolean(key && savedGamiSpecialTestResults.some((saved) => gamiSavedResultKey(saved) === key));
  });
}

function savedGamiSpecialTestResultById(resultId) {
  const id = String(resultId ?? "");
  if (!id) return null;
  return [...savedGamiSpecialTestResults, ...savedGamiSeries(savedGamiSpecialTestResults)].find((result) => String(result?.id ?? "") === id) ?? null;
}

function gamiAnalysisFromSavedResult(saved) {
  if (!saved) return null;
  const window = gamiSavedResultWindow(saved);
  if (!window) return null;
  const identity = gamiResultEngineIdentity(saved);
  const descriptors = currentGamiEngineDescriptors();
  const engine = engineById(descriptors, identity.engineId) ?? null;
  const savedResult = saved?.result_json ?? saved?.result ?? {};
  const points = gamiSweepPointsForWindow(window, engine);
  const result = gamiSweepWithTitAtPeaks({
    ...savedResult,
    engineId: savedResult.engineId ?? identity.engineId,
    engineLabel: savedResult.engineLabel ?? identity.engineLabel,
    startTime: Number.isFinite(savedResult.startTime) ? savedResult.startTime : window.startTime,
    endTime: Number.isFinite(savedResult.endTime) ? savedResult.endTime : window.endTime,
    durationSeconds: Number.isFinite(savedResult.durationSeconds) ? savedResult.durationSeconds : window.endTime - window.startTime,
  }, descriptors.length > 1 && !engine ? [] : points, savedResult.profile?.turbocharged ?? gamiAircraftProfileInfo().turbocharged);
  return {
    window,
    points,
    questions: saved?.pilot_context_json ?? saved?.pilotContext ?? {},
    result,
    engine,
    engineIdentity: identity,
    saved,
  };
}

function selectedSavedGamiSpecialTestAnalysis() {
  const saved = savedGamiSpecialTestResultById(selectedSavedGamiSpecialTestResultId);
  const analysis = gamiAnalysisFromSavedResult(saved);
  if (!analysis || !gamiSpecialTestsState.window) return null;
  const selectedKey = gamiSavedResultKey(saved);
  const currentKey = gamiAnalysisKey({ window: gamiSpecialTestsState.window, questions: gamiQuestionState() });
  const identity = gamiResultEngineIdentity(saved);
  const rounded = (value) => Math.round(Number(value) * 10) / 10;
  const sameWindow = rounded(analysis.window.startTime) === rounded(gamiSpecialTestsState.window.startTime)
    && rounded(analysis.window.endTime) === rounded(gamiSpecialTestsState.window.endTime);
  return selectedKey && (selectedKey === currentKey || (identity.engineId === "unknown" && sameWindow)) ? analysis : null;
}

function selectSavedGamiSpecialTestResult(resultId) {
  const saved = savedGamiSpecialTestResultById(resultId);
  const analysis = gamiAnalysisFromSavedResult(saved);
  if (!analysis) return;
  onOpenGamiPanel();
  selectedSavedGamiSpecialTestResultId = String(saved.id ?? "");
  selectedGamiSeriesSweepIndex = -1;
  gamiSpecialTestsState.open = true;
  gamiSpecialTestsState.selecting = false;
  gamiSpecialTestsState.window = analysis.window;
  if (analysis.engine?.id) gamiSpecialTestsState.engineId = analysis.engine.id;
  setGamiQuestionState(analysis.questions);
  gamiSpecialTestSaveState = { status: "saved", message: "Loaded saved sweep" };
  selectGamiSweepGraphSeries();
  renderGamiSpecialTestsPanel({ syncInputs: true });
  scheduleChartDraw("explorerChart");
}

function setGamiQuestionState(context = {}) {
  const setChecked = (selector, value) => {
    const input = document.querySelector(selector);
    if (input) input.checked = value === true;
  };
  setChecked("#gamiIntentionalSweep", context.intentionalSweep);
  setChecked("#gamiStartedRich", context.startedRich);
  setChecked("#gamiCompletedToPeaks", context.completedToPeaks);
  setChecked("#gamiRepeatedSweeps", context.repeatedSweeps);
  setChecked("#gamiTitManagedSweep", context.titManaged);
}

function markGamiSpecialTestUnsaved() {
  if (gamiSpecialTestSaveState.status === "saving" || gamiSpecialTestSaveState.status === "deleting") return;
  if (!gamiSpecialTestsState.window) {
    gamiSpecialTestSaveState = savedGamiSpecialTestResults.length
      ? { status: "saved", message: `${savedGamiSpecialTestResults.length} saved sweep${savedGamiSpecialTestResults.length === 1 ? "" : "s"}` }
      : { status: "idle", message: "" };
    return;
  }
  if (gamiCurrentAnalysisAlreadySaved()) {
    gamiSpecialTestSaveState = { status: "saved", message: "Already saved" };
    return;
  }
  gamiSpecialTestSaveState = { status: "dirty", message: savedGamiSpecialTestResults.length ? "Unsaved changes" : "" };
}

function currentGamiSweepAnalysis() {
  const window = gamiSpecialTestsState.window;
  if (!window) return null;
  const engine = selectedGamiEngine();
  const searchPoints = gamiSweepPointsForWindow(window, engine);
  const questions = gamiQuestionState();
  const baseResult = analyzeMarkedGamiSweep(searchPoints, {
    aircraftProfile: gamiAircraftProfile(),
    intentionalSweep: questions.intentionalSweep,
    startedRich: questions.startedRich,
    completedToPeaks: questions.completedToPeaks,
    repeatedSweeps: questions.repeatedSweeps,
    titManaged: questions.titManaged,
  });
  const result = {
    ...baseResult,
    engineId: engine?.id ?? "engine1",
    engineLabel: engine?.label ?? "Engine",
  };
  const analysisWindow = Number.isFinite(result.startTime) && Number.isFinite(result.endTime) && result.endTime > result.startTime
    ? { startTime: result.startTime, endTime: result.endTime }
    : window;
  const points = analysisWindow === window ? searchPoints : gamiSweepPointsForWindow(analysisWindow, engine);
  return { window: analysisWindow, searchWindow: window, points, searchPoints, questions, result, engine };
}

async function loadSavedGamiSpecialTests(entry) {
  clearGamiSpecialTestPersistenceState();
  const flightId = selectedDatabaseFlightId(entry);
  if (!flightId) return;
  if (savedGamiSpecialTestResultsCache.has(flightId)) {
    applySavedGamiSpecialTestResults(savedGamiSpecialTestResultsCache.get(flightId));
    return;
  }
  try {
    const response = await fetch(databaseFlightSpecialTestEndpoint(flightId, "gami-sweep"), { cache: "no-store" });
    if (getLastRequestedFlightId() !== entry.id) return;
    if (response.status === 404) {
      rememberSavedGamiSpecialTests(flightId, []);
      return;
    }
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload?.error ?? `Special test API HTTP ${response.status}`);
    const results = normalizeSavedGamiSpecialTestResults(
      payload.specialTestResults ?? [payload.specialTestResult].filter(Boolean),
    );
    rememberSavedGamiSpecialTests(flightId, results);
    applySavedGamiSpecialTestResults(results);
  } catch (error) {
    console.warn("Could not load saved GAMI sweep", error);
    gamiSpecialTestSaveState = { status: "error", message: "Saved result unavailable" };
  }
}

function syncGamiEvidencePacketMetadata(payload = {}) {
  const packet = payload.evidencePacket;
  if (!getSelectedFlight()?.database || !packet?.id) return;
  const updates = {
    currentEvidencePacketId: packet.id,
    ...(packet.evidence_sha256 ? { currentEvidenceSha256: packet.evidence_sha256 } : {}),
  };
  Object.assign(getSelectedFlight().database, updates);
  const manifestEntry = getFlightManifest().find((entry) => entry.id === getSelectedFlight().id);
  if (manifestEntry?.database) Object.assign(manifestEntry.database, updates);
}

function invalidateSelectedFlightReportAfterSpecialTest(entry = getSelectedFlight()) {
  if (!entry?.id) return;
  revokeCachedFlightReport(entry.id);
  flightReportStoredMetadata.delete(entry.id);
  const metadataKey = flightReportMetadataKey(entry);
  if (metadataKey) {
    flightReportMetadataChecked.delete(metadataKey);
    flightReportMetadataPending.delete(metadataKey);
  }
  const updates = {
    reportId: null,
    reportFileName: null,
    reportGeneratedAt: null,
    reportByteSize: null,
  };
  if (entry.database) Object.assign(entry.database, updates);
  const manifestEntry = getFlightManifest().find((item) => item.id === entry.id);
  if (manifestEntry?.database) Object.assign(manifestEntry.database, updates);
}

function applyGamiSpecialTestMutationResult(payload = {}, { message = "" } = {}) {
  if (Array.isArray(payload.specialTestResults)) {
    savedGamiSpecialTestResults = normalizeSavedGamiSpecialTestResults(payload.specialTestResults);
    // Write the mutation through to the per-flight cache so a later re-select
    // of this flight serves the post-save/post-delete results.
    rememberSavedGamiSpecialTests(selectedDatabaseFlightId(getSelectedFlight()), savedGamiSpecialTestResults);
  }
  if (selectedSavedGamiSpecialTestResultId && !savedGamiSpecialTestResultById(selectedSavedGamiSpecialTestResultId)) {
    selectedSavedGamiSpecialTestResultId = "";
  }
  syncGamiEvidencePacketMetadata(payload);
  invalidateSelectedFlightReportAfterSpecialTest(getSelectedFlight());
  if (getSelectedFlight()?.aircraft) {
    getTrendCache().delete(trendCacheKey(getSelectedFlight().aircraft));
    renderAircraftFlightsPanel(getSelectedFlight().aircraft);
  }
  resetGeneratedReport();
  renderAiSummaryPanel();
  gamiSpecialTestSaveState = {
    status: "saved",
    message: message || `${savedGamiSpecialTestResults.length} saved sweep${savedGamiSpecialTestResults.length === 1 ? "" : "s"}`,
  };
}

async function saveGamiSpecialTestResult() {
  if (appReadOnlyMode()) {
    window.alert(readOnlyActionMessage("Saving special test results"));
    syncReadOnlyUi();
    renderGamiSpecialTestsPanel({ syncInputs: false });
    return;
  }
  const flightId = selectedDatabaseFlightId();
  const analysis = currentGamiSweepAnalysis();
  if (!flightId || !analysis) return;
  const saveResults = gamiAnalysisSaveResults(analysis);
  if (!saveResults.length) return;
  if (gamiCurrentAnalysisAlreadySaved(analysis)) {
    gamiSpecialTestSaveState = { status: "saved", message: "Already saved" };
    renderGamiSpecialTestsPanel({ syncInputs: false });
    return;
  }
  const saveFlightId = getSelectedFlight()?.id ?? "";
  gamiSpecialTestSaveState = { status: "saving", message: "Saving..." };
  renderGamiSpecialTestsPanel({ syncInputs: false });
  try {
    // One request for the whole series: the server writes every sweep, then refreshes the
    // evidence packet, trend row, and snapshot once. Saving each sweep separately ran that
    // refresh once per sweep, which is what made a nine-sweep save take minutes.
    const lastResult = saveResults.at(-1)?.result;
    const engineId = analysis.engine?.id ?? lastResult?.engineId ?? "engine1";
    const engineLabel = analysis.engine?.label ?? lastResult?.engineLabel ?? "Engine";
    const response = await fetch(databaseFlightSpecialTestEndpoint(flightId, "gami-sweep"), {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        testKind: "gami-sweep",
        testName: "GAMI sweep",
        engineId,
        engineLabel,
        pilotContext: { ...analysis.questions, engineId, engineLabel },
        ...(saveResults.length > 1
          ? { results: saveResults.map((saveResult) => ({ window: saveResult.window, result: saveResult.result })) }
          : { window: saveResults[0].window, result: saveResults[0].result }),
      }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload?.error ?? `Special test API HTTP ${response.status}`);
    if (getSelectedFlight()?.id !== saveFlightId) return;
    const lastSaveResult = saveResults.at(-1);
    lastSavedGamiAnalysisKey = gamiAnalysisKey({ ...analysis, window: lastSaveResult?.window, result: lastSaveResult?.result });
    selectedSavedGamiSpecialTestResultId = String(payload?.specialTestResult?.id ?? "");
    applyGamiSpecialTestMutationResult(payload, {
      message: payload?.artifactRefreshSkipped
        ? "Saved. Re-analyze flight to update AI, trends, and reports"
        : saveResults.length > 1
        ? `Saved ${saveResults.length} sweeps for AI, trends, and reports`
        : "Saved for AI, trends, and reports",
    });
  } catch (error) {
    console.error("GAMI sweep save failed", error);
    gamiSpecialTestSaveState = { status: "error", message: error.message || "Save failed" };
  } finally {
    renderGamiSpecialTestsPanel({ syncInputs: false });
    scheduleChartDraw("explorerChart");
  }
}

async function deleteGamiSpecialTestResult(resultId) {
  if (!resultId) return;
  if (appReadOnlyMode()) {
    window.alert(readOnlyActionMessage("Deleting special test results"));
    syncReadOnlyUi();
    renderGamiSpecialTestsPanel({ syncInputs: false });
    return;
  }
  const flightId = selectedDatabaseFlightId();
  if (!flightId) return;
  const saveFlightId = getSelectedFlight()?.id ?? "";
  const deletingSelectedResult = String(resultId) === selectedSavedGamiSpecialTestResultId;
  gamiSpecialTestSaveState = { status: "deleting", message: "Deleting..." };
  renderGamiSpecialTestsPanel({ syncInputs: false });
  try {
    const response = await fetch(databaseFlightSpecialTestEndpoint(flightId, "gami-sweep", resultId), { method: "DELETE" });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload?.error ?? `Special test API HTTP ${response.status}`);
    if (getSelectedFlight()?.id !== saveFlightId) return;
    if (payload.specialTestResult && gamiSavedResultKey(payload.specialTestResult) === lastSavedGamiAnalysisKey) {
      lastSavedGamiAnalysisKey = "";
    }
    if (deletingSelectedResult) selectedSavedGamiSpecialTestResultId = "";
    applyGamiSpecialTestMutationResult(payload, { message: "Deleted saved sweep" });
  } catch (error) {
    console.error("GAMI sweep delete failed", error);
    gamiSpecialTestSaveState = { status: "error", message: error.message || "Delete failed" };
  } finally {
    renderGamiSpecialTestsPanel({ syncInputs: false });
    scheduleChartDraw("explorerChart");
  }
}

function setupGamiSpecialTests() {
  const toggle = document.querySelector("#explorerSpecialTestsToggle");
  const panel = document.querySelector("#explorerSpecialTestsPanel");
  if (!toggle || !panel) return;

  toggle.addEventListener("click", () => {
    gamiSpecialTestsState.open = !gamiSpecialTestsState.open;
    if (gamiSpecialTestsState.open) {
      onOpenGamiPanel();
      selectGamiSweepGraphSeries();
    }
    if (!gamiSpecialTestsState.open) gamiSpecialTestsState.selecting = false;
    renderGamiSpecialTestsPanel({ syncInputs: true });
    scheduleChartDraw("explorerChart");
  });

  document.querySelector("#gamiMarkSweepButton")?.addEventListener("click", () => {
    onOpenGamiPanel();
    gamiSpecialTestsState.open = true;
    gamiSpecialTestsState.selecting = !gamiSpecialTestsState.selecting;
    selectGamiSweepGraphSeries();
    renderGamiSpecialTestsPanel({ syncInputs: true });
  });

  document.querySelector("#gamiClearSweepButton")?.addEventListener("click", () => {
    gamiSpecialTestsState.window = null;
    gamiSpecialTestsState.selecting = false;
    selectedSavedGamiSpecialTestResultId = "";
    selectedGamiSeriesSweepIndex = -1;
    markGamiSpecialTestUnsaved();
    renderGamiSpecialTestsPanel({ syncInputs: true });
    scheduleChartDraw("explorerChart");
  });

  document.querySelector("#gamiSaveSweepButton")?.addEventListener("click", () => {
    void saveGamiSpecialTestResult();
  });

  document.querySelector("#gamiSavedSweepList")?.addEventListener("click", (event) => {
    const button = event.target?.closest?.("[data-gami-delete-id]");
    if (button) {
      event.preventDefault();
      void deleteGamiSpecialTestResult(button.dataset.gamiDeleteId);
      return;
    }
    const savedButton = event.target?.closest?.("[data-gami-load-id]");
    if (!savedButton) return;
    event.preventDefault();
    selectSavedGamiSpecialTestResult(savedButton.dataset.gamiLoadId);
  });

  document.querySelector("#gamiProcedureControls")?.addEventListener("click", (event) => {
    const button = event.target?.closest?.("[data-gami-question-toggle]");
    if (!button) return;
    event.preventDefault();
    const cue = gamiProcedureCueDefinitions.find((candidate) => candidate.key === button.dataset.gamiQuestionToggle);
    const input = cue ? document.querySelector(cue.inputSelector) : null;
    if (!input) return;
    input.checked = !input.checked;
    applyGamiProcedureCueChange();
  });

  document.querySelector("#gamiSweepResult")?.addEventListener("click", (event) => {
    const row = event.target?.closest?.("[data-gami-series-index]");
    if (!row) return;
    const index = Number(row.dataset.gamiSeriesIndex);
    if (!Number.isInteger(index) || index < -1 || selectedGamiSeriesSweepIndex === index) return;
    selectedGamiSeriesSweepIndex = index;
    renderGamiSpecialTestsPanel({ syncInputs: false });
  });

  document.querySelector("#gamiSweepResult")?.addEventListener("keydown", (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    const row = event.target?.closest?.("[data-gami-series-index]");
    if (!row) return;
    event.preventDefault();
    row.click();
  });

  document.querySelector("#gamiEngineSelector")?.addEventListener("click", (event) => {
    const button = event.target?.closest?.("[data-gami-engine-id]");
    if (!button) return;
    event.preventDefault();
    const descriptors = currentGamiEngineDescriptors();
    const engine = engineById(descriptors, button.dataset.gamiEngineId);
    if (!engine || gamiSpecialTestsState.engineId === engine.id) return;
    gamiSpecialTestsState.engineId = engine.id;
    selectedSavedGamiSpecialTestResultId = "";
    selectedGamiSeriesSweepIndex = -1;
    markGamiSpecialTestUnsaved();
    selectGamiSweepGraphSeries();
    renderGamiSpecialTestsPanel({ syncInputs: false });
    scheduleChartDraw("explorerChart");
  });

  ["#gamiSweepStartInput", "#gamiSweepEndInput"].forEach((selector) => {
    const input = document.querySelector(selector);
    input?.addEventListener("input", applyGamiSweepInputWindow);
    input?.addEventListener("keydown", (event) => {
      if (event.key !== "Enter") return;
      event.preventDefault();
      applyGamiSweepInputWindow();
    });
  });

  panel.querySelectorAll("[data-gami-question]").forEach((input) => {
    input.addEventListener("change", applyGamiProcedureCueChange);
  });

  renderGamiSpecialTestsPanel({ syncInputs: true });
}

function selectGamiSweepGraphSeries() {
  const available = new Set(availableSeriesKeys());
  const descriptors = currentGamiEngineDescriptors();
  const engine = selectedGamiEngine();
  const engineGamiKeys = (candidate) => [
    ...(candidate?.cylinders ?? []).map((cylinder) => cylinder.egtKey),
    candidate?.channels?.fuelFlow,
  ].filter(Boolean);
  const engineManagedKeys = (candidate) => [
    ...(candidate?.cylinders ?? []).map((cylinder) => cylinder.egtKey),
    ...(candidate?.cylinders ?? []).map((cylinder) => cylinder.chtKey),
    candidate?.channels?.fuelFlow,
    candidate?.channels?.rpm,
    candidate?.channels?.map,
    candidate?.channels?.tit,
  ].filter(Boolean);
  const selectedKeys = engine && descriptors.length > 1
    ? engineGamiKeys(engine)
    : [
      ...gamiEgtKeys(getFlightData()),
      gamiFuelFlowKey(getFlightData()),
    ].filter(Boolean);
  const candidates = selectedKeys.filter(Boolean);
  const gamiRelevantKeys = new Set(descriptors.length > 1
    ? descriptors.flatMap(engineManagedKeys)
    : [
      ...gamiEgtKeys(getFlightData()),
      ...cylinderKeys("cht"),
      gamiFuelFlowKey(getFlightData()),
      "rpm",
      "rpm1",
      "map",
      "map1",
      "tit",
      "tit1",
      "upperDeckPressure",
    ].filter(Boolean));
  let changed = false;
  gamiRelevantKeys.forEach((key) => {
    if (!activeSeries.has(key) || candidates.includes(key)) return;
    activeSeries.delete(key);
    changed = true;
  });
  [...new Set(candidates)].forEach((key) => {
    if (!available.has(key) || activeSeries.has(key)) return;
    activeSeries.add(key);
    changed = true;
  });
  if (!changed) return;
  syncSeriesControls();
  scheduleDrawAll();
}

function commitExplorerGamiSweepSelection({ startPoint, endPoint } = {}) {
  if (!Number.isFinite(startPoint?.elapsedSeconds) || !Number.isFinite(endPoint?.elapsedSeconds)) return;
  setGamiSweepWindow(startPoint.elapsedSeconds, endPoint.elapsedSeconds, { syncInputs: true });
}

function setGamiSweepWindow(rawStartTime, rawEndTime, options = {}) {
  const startTime = Number(rawStartTime);
  const endTime = Number(rawEndTime);
  if (!Number.isFinite(startTime) || !Number.isFinite(endTime) || Math.abs(endTime - startTime) < 1) return;
  gamiSpecialTestsState.open = true;
  gamiSpecialTestsState.selecting = false;
  gamiSpecialTestsState.window = {
    startTime: Math.min(startTime, endTime),
    endTime: Math.max(startTime, endTime),
  };
  selectedSavedGamiSpecialTestResultId = "";
  selectedGamiSeriesSweepIndex = -1;
  markGamiSpecialTestUnsaved();
  renderGamiSpecialTestsPanel({ syncInputs: options.syncInputs !== false });
  scheduleChartDraw("explorerChart");
}

function gamiSweepPointsForWindow(window = gamiSpecialTestsState.window, engine = selectedGamiEngine()) {
  if (!window) return [];
  const source = engine ? projectFlightDataForEngine(getFlightData(), engine) : getFlightData();
  return source.filter((point) => {
    return Number.isFinite(point?.elapsedSeconds)
      && point.elapsedSeconds >= window.startTime
      && point.elapsedSeconds <= window.endTime;
  });
}

function pointNearestElapsed(elapsedSeconds) {
  if (!Number.isFinite(elapsedSeconds) || !getFlightData().length) return null;
  let best = null;
  let bestDistance = Infinity;
  getFlightData().forEach((point) => {
    if (!Number.isFinite(point?.elapsedSeconds)) return;
    const distance = Math.abs(point.elapsedSeconds - elapsedSeconds);
    if (distance < bestDistance) {
      best = point;
      bestDistance = distance;
    }
  });
  return best;
}

function gamiTimeTextForElapsed(elapsedSeconds) {
  const point = pointNearestElapsed(elapsedSeconds);
  return point ? formatPointTime(point) : formatElapsedTime(elapsedSeconds);
}

function syncGamiSweepInputsFromWindow({ force = false } = {}) {
  const startInput = document.querySelector("#gamiSweepStartInput");
  const endInput = document.querySelector("#gamiSweepEndInput");
  const window = gamiSpecialTestsState.window;
  const setInput = (input, value) => {
    if (!input) return;
    if (!force && document.activeElement === input) return;
    input.value = value;
    input.classList.remove("is-invalid");
    input.removeAttribute("aria-invalid");
  };
  setInput(startInput, window ? gamiTimeTextForElapsed(window.startTime) : "");
  setInput(endInput, window ? gamiTimeTextForElapsed(window.endTime) : "");
}

function clockSecondsCandidates(value) {
  const text = String(value ?? "").trim().toLowerCase();
  const match = text.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(am|pm)?$/i);
  if (!match) return [];
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  const second = Number(match[3] ?? 0);
  if (hour > 24 || minute >= 60 || second >= 60) return [];
  const period = match[4]?.toLowerCase();
  if (period) {
    if (hour < 1 || hour > 12) return [];
    const baseHour = hour % 12;
    const clockHour = period === "pm" ? baseHour + 12 : baseHour;
    return [clockHour * 3600 + minute * 60 + second];
  }
  if (hour >= 24) return [];
  const primary = hour * 3600 + minute * 60 + second;
  const candidates = [primary];
  if (hour <= 12) candidates.push(((hour + 12) % 24) * 3600 + minute * 60 + second);
  return [...new Set(candidates)];
}

function clockDeltaSeconds(a, b) {
  const delta = Math.abs(a - b);
  return Math.min(delta, 86400 - delta);
}

function elapsedFromClockText(value) {
  const inputCandidates = clockSecondsCandidates(value);
  if (!inputCandidates.length) return null;
  let best = null;
  getFlightData().forEach((point) => {
    if (!Number.isFinite(point?.elapsedSeconds)) return;
    const pointCandidates = clockSecondsCandidates(formatPointTime(point));
    pointCandidates.forEach((pointSeconds) => {
      inputCandidates.forEach((inputSeconds) => {
        const delta = clockDeltaSeconds(inputSeconds, pointSeconds);
        if (!best || delta < best.delta) best = { delta, elapsedSeconds: point.elapsedSeconds };
      });
    });
  });
  return best && best.delta <= 120 ? best.elapsedSeconds : null;
}

function elapsedDurationFromText(value) {
  const text = String(value ?? "").trim();
  const match = text.match(/^(\d{1,3}):(\d{2})(?::(\d{2}))?$/);
  if (!match) return null;
  const first = Number(match[1]);
  const second = Number(match[2]);
  const third = match[3] === undefined ? null : Number(match[3]);
  if (second >= 60 || (third !== null && third >= 60)) return null;
  return third === null ? first * 60 + second : first * 3600 + second * 60 + third;
}

function elapsedFromGamiInput(value) {
  const clockElapsed = elapsedFromClockText(value);
  if (Number.isFinite(clockElapsed)) return clockElapsed;
  const durationElapsed = elapsedDurationFromText(value);
  if (!Number.isFinite(durationElapsed)) return null;
  const extent = extentFromValues(getFlightData().map((point) => point.elapsedSeconds).filter(Number.isFinite));
  if (!extent) return null;
  return durationElapsed >= extent.min - 2 && durationElapsed <= extent.max + 2 ? durationElapsed : null;
}

function applyGamiSweepInputWindow() {
  const startInput = document.querySelector("#gamiSweepStartInput");
  const endInput = document.querySelector("#gamiSweepEndInput");
  if (!startInput || !endInput) return;
  const startTime = elapsedFromGamiInput(startInput.value);
  const endTime = elapsedFromGamiInput(endInput.value);
  const markInput = (input, valid) => {
    input.classList.toggle("is-invalid", !valid);
    if (valid) input.removeAttribute("aria-invalid");
    else input.setAttribute("aria-invalid", "true");
  };
  markInput(startInput, Number.isFinite(startTime));
  markInput(endInput, Number.isFinite(endTime));
  if (!Number.isFinite(startTime) || !Number.isFinite(endTime) || endTime <= startTime) {
    renderGamiSpecialTestsPanel({ syncInputs: false });
    return;
  }
  setGamiSweepWindow(startTime, endTime, { syncInputs: false });
}

function gamiQuestionState() {
  const checked = (selector) => document.querySelector(selector)?.checked === true;
  return {
    intentionalSweep: checked("#gamiIntentionalSweep"),
    startedRich: checked("#gamiStartedRich"),
    completedToPeaks: checked("#gamiCompletedToPeaks"),
    repeatedSweeps: checked("#gamiRepeatedSweeps"),
    titManaged: checked("#gamiTitManagedSweep"),
  };
}

const gamiProcedureCueDefinitions = [
  {
    key: "intentionalSweep",
    inputSelector: "#gamiIntentionalSweep",
    label: "Intent",
    detail: "Pilot intended a GAMI sweep",
    help: "Confirms this was a deliberate mixture sweep, not ordinary leaning or power change.",
  },
  {
    key: "startedRich",
    inputSelector: "#gamiStartedRich",
    label: "Rich start",
    detail: "Started rich of peak",
    help: "Use when the sweep began rich enough to catch the first cylinder peak cleanly.",
  },
  {
    key: "completedToPeaks",
    inputSelector: "#gamiCompletedToPeaks",
    label: "Peak cue",
    detail: "Reached peaks or roughness",
    help: "Use when the pilot saw all peaks, roughness, or another end cue even if data is soft.",
  },
  {
    key: "repeatedSweeps",
    inputSelector: "#gamiRepeatedSweeps",
    label: "Repeat",
    detail: "Repeated sweep series",
    help: "Use when several passes were flown so the series average matters more than one pass.",
  },
  {
    key: "titManaged",
    inputSelector: "#gamiTitManagedSweep",
    label: "TIT plan",
    detail: "TIT-managed fast sweep",
    help: "Use for turbo operations where TIT management explains a shorter accepted sweep.",
    titOnly: true,
  },
];

function applyGamiProcedureCueChange() {
  selectedSavedGamiSpecialTestResultId = "";
  selectedGamiSeriesSweepIndex = -1;
  markGamiSpecialTestUnsaved();
  selectReportPanel("explorer");
  renderGamiSpecialTestsPanel({ syncInputs: false });
  selectReportPanel("explorer");
}

function gamiProcedureControlsHtml({ showTitQuestion = true } = {}) {
  const questions = gamiQuestionState();
  const cues = gamiProcedureCueDefinitions.filter((cue) => showTitQuestion || !cue.titOnly);
  return `
    <section class="gami-procedure-controls-inner" aria-labelledby="gamiProcedureControlsTitle">
      <header>
        <span id="gamiProcedureControlsTitle">Procedure cues</span>
      </header>
      <div class="gami-procedure-cue-layout" role="group" aria-label="GAMI procedure cue toggles">
        ${cues.map((cue) => {
          const active = questions[cue.key] === true;
          return `
            <div class="gami-procedure-cue-row">
              <button class="${active ? "is-pass" : "is-open"}" type="button" aria-pressed="${active ? "true" : "false"}" data-gami-question-toggle="${escapeHtml(cue.key)}" title="${escapeHtml(cue.detail)}" aria-label="${escapeHtml(`${cue.label}: ${cue.detail}`)}">
                <i aria-hidden="true">${active ? "✓" : ""}</i>
                <strong>${escapeHtml(cue.label)}</strong>
              </button>
              <span class="gami-procedure-cue-help">${escapeHtml(cue.help ?? cue.detail)}</span>
            </div>
          `;
        }).join("")}
      </div>
    </section>
  `;
}

function gamiEngineSelectorHtml() {
  const descriptors = currentGamiEngineDescriptors();
  if (descriptors.length <= 1) return "";
  const selectedId = ensureSelectedGamiEngineId(descriptors);
  return `
    <div class="special-test-engine-selector-inner" role="radiogroup" aria-label="GAMI engine">
      <span>Engine</span>
      ${descriptors.map((engine) => {
        const selected = engine.id === selectedId;
        return `
          <button type="button" role="radio" aria-checked="${selected ? "true" : "false"}" aria-pressed="${selected ? "true" : "false"}" class="${selected ? "is-active" : ""}" data-gami-engine-id="${escapeHtml(engine.id)}">
            ${escapeHtml(gamiEngineOptionLabel(engine))}
          </button>
        `;
      }).join("")}
    </div>
  `;
}

function gamiNumber(value, digits = 1, suffix = "") {
  return Number.isFinite(value) ? `${Number(value).toFixed(digits)}${suffix}` : "--";
}

function gamiSeriesAverageSpread(result) {
  const spreads = (Array.isArray(result?.seriesSweeps) ? result.seriesSweeps : [])
    .map((sweep) => sweep?.spreadGph)
    .filter(Number.isFinite);
  if (!spreads.length) return null;
  return Number((spreads.reduce((total, value) => total + value, 0) / spreads.length).toFixed(2));
}

function gamiSeriesAverageResult(result) {
  const spreadGph = gamiSeriesAverageSpread(result);
  return Number.isFinite(spreadGph)
    ? { ...result, spreadGph, seriesAverage: true }
    : result;
}

function gamiSpreadFinding(result) {
  const spread = Number(result?.spreadGph);
  if (!Number.isFinite(spread)) {
    return {
      className: "is-muted",
      label: "Spread unavailable",
      detail: "Mark a sweep window with usable EGT and fuel-flow data.",
    };
  }
  if (spread <= 0.6) {
    return {
      className: "is-good",
      label: "Good spread",
      detail: "At or below 0.60 GPH display target.",
    };
  }
  if (spread <= 1.0) {
    return {
      className: "is-watch",
      label: "Watch spread",
      detail: "Above 0.60 GPH; compare with prior saved sweeps.",
    };
  }
  return {
    className: "is-review",
    label: "Wide spread",
    detail: "Above 1.00 GPH review range.",
  };
}

const gamiSpreadToneColors = Object.freeze({ "is-good": "#3fb27f", "is-watch": "#e7a13a", "is-review": "#e5544b" });

// Spread strip: the shared lean-find strip (src/app/lean-find-strip.ts) with
// GAMI's own band tone and 0.60 GPH target. Built entirely from the persisted
// peaks array, so it renders for saved sweeps too.
function gamiSpreadStripHtml(result) {
  const finding = gamiSpreadFinding(result);
  return leanFindStripHtml({
    peaks: gamiSweepPeakDisplayRows(result, currentGamiEngineDescriptors()).map((peak) => ({
      cylinder: peak.displayCylinder,
      label: peak.displayCylinderLabel,
      peakFuelFlowGph: peak.peakFuelFlowGph,
      solid: peak.clear && peak.peakStrength !== "soft",
    })),
    spreadGph: result?.spreadGph,
    bandColor: gamiSpreadToneColors[finding.className] ?? "#9fb0c4",
  });
}

function gamiConfidenceTone(confidence) {
  const value = String(confidence ?? "").toLowerCase();
  if (value === "high") return "good";
  if (value === "medium") return "watch";
  if (value === "low") return "review";
  return "muted";
}

function gamiPeakTone(result) {
  const clear = Number(result?.clearPeakCount);
  const total = Number(result?.peakCount);
  if (!Number.isFinite(clear) || !Number.isFinite(total) || total <= 0) return "muted";
  if (clear === total) return "good";
  if (clear >= Math.max(2, total - 2)) return "watch";
  return "review";
}

function gamiDurationTone(result) {
  const band = String(result?.durationBand ?? "").toLowerCase();
  if (band === "deliberate" || band === "turbo-fast") return "good";
  if (band === "short") return "watch";
  if (band === "too-fast") return "review";
  return "muted";
}

function gamiPowerTone(power) {
  if (!power) return "muted";
  const percentRange = Number(power.percentPower?.range);
  const rpmRange = Number(power.rpm?.range);
  const mapRange = Number(power.map?.range);
  if (
    (!Number.isFinite(percentRange) || percentRange <= 3) &&
    (!Number.isFinite(rpmRange) || rpmRange <= 50) &&
    (!Number.isFinite(mapRange) || mapRange <= 0.5)
  ) return "good";
  return "watch";
}

function gamiSeriesTone(result, questions = gamiQuestionState()) {
  const count = Number(result?.seriesSweepCount ?? (Array.isArray(result?.seriesSweeps) ? result.seriesSweeps.length : 0));
  if (count > 1) return "good";
  if (questions.repeatedSweeps || result?.procedure?.repeatedSweeps) return "watch";
  return "muted";
}

function gamiFuelFlowText(result) {
  const fuel = result?.fuel;
  if (!fuel) return "Fuel-flow trend unavailable";
  const monotonicText = Number.isFinite(fuel.monotonicShare)
    ? `${gamiNumber(fuel.monotonicShare * 100, 0, "%")} decreasing`
    : "decreasing share unavailable";
  return `${gamiNumber(fuel.startGph, 1, " GPH")} to ${gamiNumber(fuel.endGph, 1, " GPH")} · ${monotonicText}`;
}

function gamiFuelFlowValue(result) {
  const fuel = result?.fuel;
  return fuel ? `${gamiNumber(fuel.startGph, 1, " GPH")} to ${gamiNumber(fuel.endGph, 1, " GPH")}` : "--";
}

function gamiFuelFlowDetail(result) {
  const fuel = result?.fuel;
  if (!fuel) return "Fuel-flow trend unavailable";
  const monotonicText = Number.isFinite(fuel.monotonicShare)
    ? `${gamiNumber(fuel.monotonicShare * 100, 0, "%")} decreasing`
    : "decreasing share unavailable";
  const reversals = Number.isFinite(fuel.reversalCount)
    ? `${fuel.reversalCount} reversal${fuel.reversalCount === 1 ? "" : "s"}`
    : "reversal count unavailable";
  return `${monotonicText} · ${reversals}`;
}

function gamiMetricCardHtml({ label, value, detail, tone = "muted" } = {}) {
  return `
    <div class="special-test-metric-card is-${escapeHtml(tone)}">
      <span>${escapeHtml(label)}</span>
      <strong>${escapeHtml(value)}</strong>
      <small>${escapeHtml(detail)}</small>
    </div>
  `;
}

function gamiOverviewCardsHtml(result, heroResult, showingSeriesAverage, seriesSweeps = [], questions = gamiQuestionState()) {
  const confidence = String(result?.confidence ?? "--");
  const peaksValue = Number.isFinite(result?.clearPeakCount) && Number.isFinite(result?.peakCount)
    ? `${result.clearPeakCount}/${result.peakCount}`
    : "--";
  const durationText = Number.isFinite(result?.durationSeconds) ? formatElapsedTime(result.durationSeconds) : "--";
  const durationDetail = String(result?.durationBand ?? "unknown").replace("-", " ");
  const sweepCount = showingSeriesAverage ? `${seriesSweeps.length} detected sweeps` : "selected sweep";
  const seriesCount = Number(result?.seriesSweepCount ?? seriesSweeps.length);
  const repeatValue = seriesCount > 1 ? `${seriesCount} sweeps` : (questions.repeatedSweeps || result?.procedure?.repeatedSweeps ? "Reported" : "Single");
  const repeatDetail = seriesCount > 1 ? "detected inside marked window" : gamiProcedureRepeatText(result?.procedure ?? questions, questions);
  return `
    <div class="special-test-overview-grid" aria-label="GAMI result and trust summary">
      ${gamiMetricCardHtml({
        label: "Confidence",
        value: confidence,
        detail: result?.confidenceDetail ?? sweepCount,
        tone: gamiConfidenceTone(confidence),
      })}
      ${gamiMetricCardHtml({
        label: "Cylinder peaks",
        value: peaksValue,
        detail: "clear peak cues",
        tone: gamiPeakTone(result),
      })}
      ${gamiMetricCardHtml({
        label: "Duration",
        value: durationText,
        detail: durationDetail,
        tone: gamiDurationTone(result),
      })}
      ${gamiMetricCardHtml({
        label: "Fuel flow",
        value: gamiFuelFlowValue(result),
        detail: gamiFuelFlowDetail(result),
        tone: Number.isFinite(heroResult?.spreadGph) ? gamiSpreadFinding(heroResult).className.replace("is-", "") : "muted",
      })}
      ${gamiMetricCardHtml({
        label: "Power",
        value: result?.power?.percentPower ? `${Math.round(result.power.percentPower.avg)}% avg` : "Context",
        detail: gamiPowerText(result.power),
        tone: gamiPowerTone(result.power),
      })}
      ${gamiMetricCardHtml({
        label: "Repeatability",
        value: repeatValue,
        detail: repeatDetail,
        tone: gamiSeriesTone(result, questions),
      })}
    </div>
  `;
}

function gamiExceptionsHtml(result) {
  if (!result || result.status === "missing-data") return "";
  const notes = [];
  if (result.status !== "complete") notes.push(result.confidenceDetail || result.label || "Sweep evidence is limited.");
  if (Number.isFinite(result.clearPeakCount) && Number.isFinite(result.peakCount) && result.clearPeakCount < result.peakCount) {
    notes.push(`${result.peakCount - result.clearPeakCount} cylinder peak cue${result.peakCount - result.clearPeakCount === 1 ? "" : "s"} limited or missing.`);
  }
  if (result.durationBand === "short" || result.durationBand === "too-fast") notes.push("Marked window is shorter than the ideal slow sweep.");
  if (Number(result.fuel?.reversalCount) > 0) notes.push(`${result.fuel.reversalCount} fuel-flow reversal${result.fuel.reversalCount === 1 ? "" : "s"} inside the marked window.`);
  if (!notes.length) notes.push("No major exceptions detected for the selected sweep.");
  return `
    <section class="special-test-exceptions" aria-label="GAMI interpretation notes">
      <span>Exceptions</span>
      <ul>
        ${notes.slice(0, 4).map((note) => `<li>${escapeHtml(note)}</li>`).join("")}
      </ul>
    </section>
  `;
}

function gamiPowerText(power) {
  if (!power) return "Power context unavailable";
  const parts = [
    power.percentPower ? `${Math.round(power.percentPower.avg)}% power, range ${gamiNumber(power.percentPower.range, 1, "%")}` : "",
    power.rpm ? `RPM range ${gamiNumber(power.rpm.range, 0)}` : "",
    power.map ? `MAP range ${gamiNumber(power.map.range, 1, " inHg")}` : "",
  ].filter(Boolean);
  return parts.join(" · ") || "Power context unavailable";
}

function gamiProcedureIntentText(procedure, questions) {
  if (procedure?.intentionalSweep || questions.intentionalSweep) return "Intentional GAMI sweep confirmed";
  return "Pilot intent not confirmed";
}

function gamiProcedureStartText(procedure, questions) {
  if (procedure?.startedRichByData === true && (procedure.startedRich || questions.startedRich)) {
    return `Pilot confirmed; data starts ${gamiNumber(procedure.richStartMarginGph, 2, " GPH")} rich of first peak`;
  }
  if (procedure?.startedRichByData === true) {
    return `Data starts ${gamiNumber(procedure.richStartMarginGph, 2, " GPH")} rich of first peak`;
  }
  if ((procedure?.startedRich || questions.startedRich) && procedure?.startedRichByData === false) {
    return "Pilot confirmed, but marked window starts past first peak";
  }
  if (procedure?.startedRich || questions.startedRich) return "Pilot confirmed rich-of-peak start";
  return "Rich-of-peak start not confirmed";
}

function gamiProcedureCompletionText(result, procedure, questions) {
  if (result.complete && (procedure?.completedToPeaks || questions.completedToPeaks)) {
    return "Pilot confirmed; all cylinder peaks detected";
  }
  if (result.complete) return "All cylinder peaks detected";
  if (procedure?.completedToPeaks || questions.completedToPeaks) {
    return `Pilot reports peaks/roughness; data saw ${result.clearPeakCount}/${result.peakCount} peaks`;
  }
  return `${result.clearPeakCount}/${result.peakCount} clear peak${result.peakCount === 1 ? "" : "s"} detected`;
}

function gamiProcedureRepeatText(procedure, questions) {
  if (procedure?.repeatedSweeps || questions.repeatedSweeps) return "Pilot reports repeated sweep series";
  return "Single marked sweep";
}

function gamiProcedureTitText(result, procedure, questions) {
  if (procedure?.titManaged || questions.titManaged) {
    if (result.profile?.turbocharged && result.durationBand === "turbo-fast") return "TIT-managed turbo sweep accepted as fast";
    if (result.profile?.turbocharged) return "TIT-managed turbo sweep reported";
    return "TIT-managed sweep reported";
  }
  return result.profile?.turbocharged ? "Standard duration expectation unless TIT-managed" : "Standard duration expectation";
}

function gamiQualityRows(result, questions = gamiQuestionState()) {
  if (!result || result.status === "missing-data") return [];
  const profile = result.profile ?? {};
  const procedure = result.procedure ?? questions;
  const profileText = profile.fuelInjected
    ? "Fuel-injected profile"
    : profile.carbureted ? "Carbureted profile; injector-balance language should stay off" : "Fuel-system profile not set";
  const durationText = {
    deliberate: "Deliberate sweep duration",
    "turbo-fast": "Short TIT-managed turbo sweep",
    short: "Shorter than the ideal 3-4 minute sweep",
    "too-fast": "Very short sweep",
    unknown: "Duration unavailable",
  }[result.durationBand] ?? "Duration checked";
  const fuel = result.fuel;
  const monotonicText = Number.isFinite(fuel?.monotonicShare)
    ? `${gamiNumber(fuel.monotonicShare * 100, 0, "%")} decreasing`
    : "decreasing share unavailable";
  const fuelText = fuel
    ? `FF ${gamiNumber(fuel.startGph, 1, " GPH")} to ${gamiNumber(fuel.endGph, 1, " GPH")} · ${monotonicText} · ${fuel.reversalCount} reversal${fuel.reversalCount === 1 ? "" : "s"}`
    : "Fuel-flow trend unavailable";
  const detection = result.sweepDetection ?? {};
  const detectionRows = detection.adjusted
    ? [[
      detection.series ? "Detected series" : "Detected sweep",
      detection.series && Number.isFinite(result.seriesSweepCount)
        ? `${result.seriesSweepCount} sweeps inside marked window`
        : `${formatElapsedTime(result.startTime)}-${formatElapsedTime(result.endTime)} inside marked window`,
    ]]
    : [];
  return [
    ["Profile", profileText],
    ...detectionRows,
    ["Pilot intent", gamiProcedureIntentText(procedure, questions)],
    ["Start rich", gamiProcedureStartText(procedure, questions)],
    ["Completion cue", gamiProcedureCompletionText(result, procedure, questions)],
    ["Repeatability", gamiProcedureRepeatText(procedure, questions)],
    ["TIT strategy", gamiProcedureTitText(result, procedure, questions)],
    ["Fuel flow", fuelText],
    ["Cylinder peaks", `${result.clearPeakCount}/${result.peakCount} clear peak${result.peakCount === 1 ? "" : "s"}`],
    ["Power", gamiPowerText(result.power)],
    ["Duration", `${formatElapsedTime(result.durationSeconds)} · ${durationText}`],
  ];
}

function gamiDiagnosticReadoutHtml(result, questions = gamiQuestionState()) {
  const rows = gamiQualityRows(result, questions);
  if (!rows.length) return "";
  const priority = new Set(["Profile", "Detected series", "Detected sweep", "Start rich", "Completion cue", "TIT strategy", "Fuel flow", "Power"]);
  const visibleRows = [
    ...rows.filter(([label]) => priority.has(label)),
    ...rows.filter(([label]) => !priority.has(label)),
  ].slice(0, 8);
  return `
    <section class="special-test-diagnostic-readout" aria-label="GAMI diagnostic readout">
      <span>Diagnostic readout</span>
      <dl>
        ${visibleRows.map(([label, value]) => `
          <div>
            <dt>${escapeHtml(label)}</dt>
            <dd>${escapeHtml(value)}</dd>
          </div>
        `).join("")}
      </dl>
    </section>
  `;
}

function gamiPeakRowsHtml(result, options = {}) {
  const rows = gamiSweepPeakDisplayRows(result, currentGamiEngineDescriptors()).slice().sort((a, b) => {
    if (Number.isFinite(b.peakFuelFlowGph) && Number.isFinite(a.peakFuelFlowGph) && b.peakFuelFlowGph !== a.peakFuelFlowGph) {
      return b.peakFuelFlowGph - a.peakFuelFlowGph;
    }
    return (a.displayCylinder ?? a.cylinder ?? 0) - (b.displayCylinder ?? b.cylinder ?? 0);
  });
  if (!rows.length) return "";
  const tableLabel = options.label ?? "GAMI peak fuel-flow evidence";
  const showTit = result?.profile?.turbocharged === true;
  return `
    <div class="special-test-peak-table special-test-cylinder-peak-table ${showTit ? "has-tit" : ""}" role="table" aria-label="${escapeHtml(tableLabel)}">
      <div role="row" class="special-test-peak-heading">
        <span role="columnheader">Cyl</span>
        <span role="columnheader">Peak FF</span>
        <span role="columnheader">Peak EGT</span>
        ${showTit ? '<span role="columnheader">TIT at EGT peak</span>' : ""}
        <span role="columnheader">Time</span>
        <span role="columnheader">Cue</span>
      </div>
      ${rows.map((peak) => {
        const time = Number.isFinite(peak.peakElapsedSeconds)
          ? gamiTimeTextForElapsed(peak.peakElapsedSeconds)
          : "--";
        const cue = peak.clear
          ? (peak.peakStrength === "soft" ? "Soft peak" : "Clear")
          : peak.reason || "Limited";
        return `
          <div role="row" class="${peak.clear ? "is-clear" : "is-limited"}">
            <span role="cell">${escapeHtml(peak.displayCylinderLabel ?? (peak.cylinder ? `#${peak.cylinder}` : peak.key))}</span>
            <span role="cell">${escapeHtml(gamiNumber(peak.peakFuelFlowGph, 2, " GPH"))}</span>
            <span role="cell">${escapeHtml(gamiNumber(peak.peakEgtF, 0, " F"))}</span>
            ${showTit ? `<span role="cell" data-gami-tit>${escapeHtml(gamiNumber(peak.peakTitF, 0, " F"))}</span>` : ""}
            <span role="cell">${escapeHtml(time)}</span>
            <span role="cell">${escapeHtml(cue)}</span>
          </div>
        `;
      }).join("")}
    </div>
  `;
}

function gamiSeriesRowsHtml(result) {
  const sweeps = Array.isArray(result?.seriesSweeps) ? result.seriesSweeps : [];
  if (sweeps.length <= 1) return "";
  const selectedIndex = Math.max(-1, Math.min(selectedGamiSeriesSweepIndex, sweeps.length - 1));
  const avgSpread = gamiSeriesAverageSpread(result);
  return `
    <div class="special-test-peak-table special-test-series-table" role="table" aria-label="Detected repeated GAMI sweeps">
      <div role="row" class="special-test-peak-heading">
        <span role="columnheader">Sweep</span>
        <span role="columnheader">Window</span>
        <span role="columnheader">Spread</span>
        <span role="columnheader">Conf</span>
        <span role="columnheader">Peaks</span>
      </div>
      <div role="row" tabindex="0" aria-selected="${selectedIndex === -1 ? "true" : "false"}" class="is-average ${selectedIndex === -1 ? "is-selected" : ""}" data-gami-series-index="-1">
        <span role="cell">Avg</span>
        <span role="cell">Series</span>
        <span role="cell">${escapeHtml(gamiNumber(avgSpread, 2, " GPH"))}</span>
        <span role="cell">--</span>
        <span role="cell">${escapeHtml(`${sweeps.length} sweeps`)}</span>
      </div>
      ${sweeps.map((sweep, index) => {
        const selected = index === selectedIndex;
        return `
        <div role="row" tabindex="0" aria-selected="${selected ? "true" : "false"}" class="${sweep.status === "complete" ? "is-clear" : "is-limited"} ${selected ? "is-selected" : ""}" data-gami-series-index="${escapeHtml(String(index))}">
          <span role="cell">${escapeHtml(String(index + 1))}</span>
          <span role="cell">${escapeHtml(`${gamiTimeTextForElapsed(sweep.startTime)}-${gamiTimeTextForElapsed(sweep.endTime)}`)}</span>
          <span role="cell">${escapeHtml(gamiNumber(sweep.spreadGph, 2, " GPH"))}</span>
          <span role="cell">${escapeHtml(sweep.confidence ?? "--")}</span>
          <span role="cell">${escapeHtml(`${sweep.clearPeakCount ?? "--"}/${sweep.peakCount ?? "--"}`)}</span>
        </div>
      `;
      }).join("")}
    </div>
  `;
}

function gamiSweepResultHtml() {
  const analysis = selectedSavedGamiSpecialTestAnalysis() ?? currentGamiSweepAnalysis();
  if (!analysis) {
    return '<p class="special-test-empty">No sweep window marked.</p>';
  }
  const { result, questions } = analysis;
  const seriesSweeps = Array.isArray(result.seriesSweeps) ? result.seriesSweeps : [];
  if (seriesSweeps.length > 1) {
    if (selectedGamiSeriesSweepIndex >= seriesSweeps.length || selectedGamiSeriesSweepIndex < -1) selectedGamiSeriesSweepIndex = -1;
  } else if (selectedGamiSeriesSweepIndex !== -1) {
    selectedGamiSeriesSweepIndex = -1;
  }
  const showingSeriesAverage = seriesSweeps.length > 1 && selectedGamiSeriesSweepIndex === -1;
  const selectedSeriesSweep = seriesSweeps.length > 1 && !showingSeriesAverage
    ? (seriesSweeps[selectedGamiSeriesSweepIndex] ?? seriesSweeps[0])
    : result;
  const heroResult = showingSeriesAverage ? gamiSeriesAverageResult(result) : selectedSeriesSweep;
  const identity = gamiResultEngineIdentity({ result_json: result, pilot_context_json: questions });
  const engineHeading = currentGamiEngineDescriptors().length > 1 ? `
    <div class="special-test-engine-heading">
      <span>Engine</span>
      <strong>${escapeHtml(identity.engineLabel)}</strong>
    </div>
  ` : "";
  if (result.status === "missing-data") {
    return `
      ${engineHeading}
      <div class="special-test-result-summary is-muted">
        <div><span>Status</span><strong>${escapeHtml(result.label)}</strong></div>
        <div><span>Window</span><strong>${escapeHtml(formatElapsedTime(result.durationSeconds ?? 0))}</strong></div>
        <div><span>Samples</span><strong>${escapeHtml(String(result.sampleCount ?? 0))}</strong></div>
      </div>
      <p class="special-test-empty">Missing ${escapeHtml(result.missing.join(", "))}.</p>
    `;
  }

  const spreadFinding = gamiSpreadFinding(heroResult);
  const heroLabel = showingSeriesAverage ? "GAMI Spread Avg" : "GAMI Spread";
  const heroDetail = showingSeriesAverage
    ? `Average across ${seriesSweeps.length} detected sweeps.`
    : spreadFinding.detail;
  return `
    ${engineHeading}
    <div class="special-test-result-focus">
      <section class="special-test-spread-hero ${spreadFinding.className}" aria-label="GAMI spread finding">
        <div>
          <span>${escapeHtml(heroLabel)}</span>
          <strong>${escapeHtml(gamiNumber(heroResult.spreadGph, 2, " GPH"))}</strong>
          <small>${escapeHtml(heroDetail)}</small>
        </div>
        <b>${escapeHtml(showingSeriesAverage ? "Series average" : spreadFinding.label)}</b>
      </section>
      <div class="special-test-result-side">
        ${gamiExceptionsHtml(selectedSeriesSweep)}
      </div>
      ${gamiDiagnosticReadoutHtml(selectedSeriesSweep, questions)}
    </div>
    ${gamiSpreadStripHtml(selectedSeriesSweep)}
    ${gamiOverviewCardsHtml(selectedSeriesSweep, heroResult, showingSeriesAverage, seriesSweeps, questions)}
    ${gamiSeriesRowsHtml(result)}
    ${gamiPeakRowsHtml(selectedSeriesSweep, {
      label: seriesSweeps.length > 1
        ? (showingSeriesAverage ? "GAMI peak fuel-flow evidence for series representative" : `GAMI peak fuel-flow evidence for sweep ${selectedGamiSeriesSweepIndex + 1}`)
        : "GAMI peak fuel-flow evidence",
    })}
  `;
}

function gamiSavedSweepListHtml() {
  if (!savedGamiSpecialTestResults.length) return "";
  const averages = savedGamiSeries(savedGamiSpecialTestResults);
  const itemHtml = [...savedGamiSpecialTestResults, ...averages].map((saved, index) => {
    const result = saved.result_json ?? {};
    const identity = gamiResultEngineIdentity(saved);
    const enginePrefix = currentGamiEngineDescriptors().length > 1
      ? `${String(identity.engineLabel).replace(/\s+Engine$/i, "")} `
      : "";
    const window = gamiSavedResultWindow(saved);
    const windowText = window
      ? `${gamiTimeTextForElapsed(window.startTime)}-${gamiTimeTextForElapsed(window.endTime)}`
      : "Marked window";
    const spread = Number.isFinite(result.spreadGph) ? `${Number(result.spreadGph).toFixed(2)} GPH` : "Spread unavailable";
    const confidence = result.confidence ? String(result.confidence) : "Confidence n/a";
    const peaks = Number.isFinite(result.clearPeakCount) && Number.isFinite(result.peakCount)
      ? `${result.clearPeakCount}/${result.peakCount} peaks`
      : "Peaks n/a";
    const savedAt = gamiSavedAtLabel(saved);
    const selected = String(saved.id ?? "") === selectedSavedGamiSpecialTestResultId;
    const label = saved.isSeriesAverage
      ? `${enginePrefix}Saved sweeps average (${result.seriesSweeps.length} sweeps): ${windowText}`
      : `${enginePrefix}Sweep ${index + 1}: ${windowText}`;
    return `
      <div class="special-test-saved-item ${selected ? "is-selected" : ""}">
        <button class="special-test-saved-load" type="button" data-gami-load-id="${escapeHtml(saved.id)}" aria-label="${escapeHtml(`Show ${label}`)}">
          <strong>${escapeHtml(label)}</strong>
          <span>${escapeHtml([spread, confidence, peaks, savedAt ? `Saved ${savedAt}` : ""].filter(Boolean).join(" · "))}</span>
        </button>
        ${saved.isSeriesAverage ? "" : `<button class="special-test-saved-delete" type="button" data-gami-delete-id="${escapeHtml(saved.id)}" aria-label="${escapeHtml(`Delete saved GAMI sweep ${windowText}`)}">&times;</button>`}
      </div>
    `;
  }).join("");
  return `
    <section class="special-test-saved-list-inner" aria-label="Saved GAMI sweep results">
      <div class="special-test-saved-heading">
        <span>Saved sweeps</span>
        <strong>${escapeHtml(String(savedGamiSpecialTestResults.length))}</strong>
      </div>
      <div class="special-test-saved-items">${itemHtml}</div>
    </section>
  `;
}

function gamiActionButtonHtml(action, label) {
  const icons = {
    mark: `
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M4 7V5a1 1 0 0 1 1-1h2"></path>
        <path d="M17 4h2a1 1 0 0 1 1 1v2"></path>
        <path d="M20 17v2a1 1 0 0 1-1 1h-2"></path>
        <path d="M7 20H5a1 1 0 0 1-1-1v-2"></path>
        <path d="M8 12h8"></path>
        <path d="M12 8v8"></path>
      </svg>
    `,
    save: `
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M5 4h12l2 2v14H5z"></path>
        <path d="M8 4v6h8V4"></path>
        <path d="M8 20v-6h8v6"></path>
      </svg>
    `,
    clear: `
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M18 6 6 18"></path>
        <path d="m6 6 12 12"></path>
      </svg>
    `,
  };
  const text = label ? `<span>${escapeHtml(label)}</span>` : "";
  return `${icons[action] ?? ""}${text}`;
}

function renderGamiSpecialTestsPanel(options = {}) {
  const panel = document.querySelector("#explorerSpecialTestsPanel");
  const toggle = document.querySelector("#explorerSpecialTestsToggle");
  const markButton = document.querySelector("#gamiMarkSweepButton");
  const clearButton = document.querySelector("#gamiClearSweepButton");
  const saveButton = document.querySelector("#gamiSaveSweepButton");
  const saveStatus = document.querySelector("#gamiSavedSweepStatus");
  const engineSelectorEl = document.querySelector("#gamiEngineSelector");
  const resultEl = document.querySelector("#gamiSweepResult");
  const savedListEl = document.querySelector("#gamiSavedSweepList");
  const procedureControlsEl = document.querySelector("#gamiProcedureControls");
  const hint = document.querySelector(".explorer-chart-wrap .chart-gesture-hint");
  const canvas = document.querySelector("#explorerChart");
  const titQuestion = document.querySelector("#gamiTitManagedSweep")?.closest("label");
  const profile = gamiAircraftProfileInfo();
  const showTitQuestion = profile.turbocharged || gamiLoggedTitAvailable();

  if (toggle) {
    toggle.classList.toggle("is-active", gamiSpecialTestsState.open);
    toggle.setAttribute("aria-expanded", gamiSpecialTestsState.open ? "true" : "false");
  }
  if (panel) {
    panel.hidden = !gamiSpecialTestsState.open;
    panel.classList.toggle("is-selecting", gamiSpecialTestsState.selecting);
  }
  if (!gamiSpecialTestsState.open) hideExplorerGamiCursorTime();
  if (markButton) {
    markButton.classList.toggle("is-active", gamiSpecialTestsState.selecting);
    markButton.setAttribute("aria-pressed", gamiSpecialTestsState.selecting ? "true" : "false");
    markButton.setAttribute("aria-label", gamiSpecialTestsState.selecting ? "Marking GAMI sweep window" : "Mark GAMI sweep window");
    markButton.setAttribute("title", gamiSpecialTestsState.selecting ? "Marking sweep" : "Mark sweep window");
    markButton.innerHTML = gamiActionButtonHtml("mark", gamiSpecialTestsState.selecting ? "Marking" : "Mark");
  }
  if (clearButton) clearButton.disabled = !gamiSpecialTestsState.window;
  const currentAnalysis = gamiSpecialTestsState.window ? currentGamiSweepAnalysis() : null;
  const currentAlreadySaved = currentAnalysis ? gamiCurrentAnalysisAlreadySaved(currentAnalysis) : false;
  if (saveButton) {
    saveButton.disabled = !gamiSpecialTestsState.window
      || !selectedDatabaseFlightId()
      || gamiSpecialTestSaveState.status === "saving"
      || gamiSpecialTestSaveState.status === "deleting"
      || currentAlreadySaved
      || appReadOnlyMode();
  }
  if (saveStatus) {
    const fallbackMessage = !selectedDatabaseFlightId()
      ? "Save requires a persisted flight"
      : appReadOnlyMode()
        ? "Read-only mode"
        : savedGamiSpecialTestResults.length && !gamiSpecialTestSaveState.message
          ? `${savedGamiSpecialTestResults.length} saved sweep${savedGamiSpecialTestResults.length === 1 ? "" : "s"}`
          : "";
    saveStatus.textContent = gamiSpecialTestSaveState.message || fallbackMessage;
    saveStatus.classList.toggle("is-saved", gamiSpecialTestSaveState.status === "saved");
    saveStatus.classList.toggle("is-error", gamiSpecialTestSaveState.status === "error");
  }
  if (engineSelectorEl) {
    const selectorHtml = gamiEngineSelectorHtml();
    engineSelectorEl.innerHTML = selectorHtml;
    engineSelectorEl.hidden = !selectorHtml;
  }
  if (titQuestion) titQuestion.hidden = !showTitQuestion;
  if (procedureControlsEl) procedureControlsEl.innerHTML = gamiProcedureControlsHtml({ showTitQuestion });
  if (savedListEl) savedListEl.innerHTML = gamiSavedSweepListHtml();
  if (resultEl) resultEl.innerHTML = gamiSweepResultHtml();
  if (options.syncInputs !== false) syncGamiSweepInputsFromWindow({ force: true });

  const selecting = gamiSpecialTestsState.open && gamiSpecialTestsState.selecting;
  if (hint && !getExplorerPilotObservationSelectionActive()) {
    hint.textContent = selecting ? "Drag to mark GAMI sweep" : chartGestureHintBaseText();
  }
  canvas?.classList.toggle("is-marking-special-test", selecting);
}

  return {
    clearGamiSpecialTestPersistenceState,
    commitExplorerGamiSweepSelection,
    loadSavedGamiSpecialTests,
    renderGamiSpecialTestsPanel,
    resetGamiSpecialTestSelectionState,
    savedGamiSpecialTestResultById,
    selectGamiSweepGraphSeries,
    selectSavedGamiSpecialTestResult,
    setGamiQuestionState,
    setupGamiSpecialTests,
    specialTestDetectionWindows,
    getSavedGamiSpecialTestResults,
    getSelectedSavedGamiSpecialTestResultId,
  };
}
